import { createContext, useContext, useState, type ReactNode } from "react";

/** Buffered wizard state shared across onboarding steps.
 *  Survives step navigation without a reload. Context answers are buffered
 *  here until company creation, then flushed to the server. */
export interface OnboardingWizardState {
  // Context step (buffered until company creation)
  industry: string;
  role: string;
  intents: string[];

  // Plan step
  selectedPlanId: string | null;
  selectedCycle: "monthly" | "annual";

  // Setters
  setIndustry: (v: string) => void;
  setRole: (v: string) => void;
  setIntents: (v: string[]) => void;
  setSelectedPlanId: (v: string | null) => void;
  setSelectedCycle: (v: "monthly" | "annual") => void;
}

const OnboardingStateContext = createContext<OnboardingWizardState | null>(null);

export function OnboardingStateProvider({ children }: { children: ReactNode }) {
  const [industry, setIndustry] = useState("");
  const [role, setRole] = useState("");
  const [intents, setIntents] = useState<string[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<"monthly" | "annual">("monthly");

  return (
    <OnboardingStateContext.Provider
      value={{
        industry,
        role,
        intents,
        selectedPlanId,
        selectedCycle,
        setIndustry,
        setRole,
        setIntents,
        setSelectedPlanId,
        setSelectedCycle,
      }}
    >
      {children}
    </OnboardingStateContext.Provider>
  );
}

export function useOnboardingState(): OnboardingWizardState {
  const ctx = useContext(OnboardingStateContext);
  if (!ctx) throw new Error("useOnboardingState must be inside OnboardingStateProvider");
  return ctx;
}
